import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import growthbookFile from '../../../../shared/constants/growthbook';
import useFeaturesReady from '../../../../shared/featureFlags/useFeaturesReady';
import usePlatform from '../../../../hooks/usePlatform';
import type { RootState } from '../../../../redux/store';
import {
	useOnboarding,
	useUpdateOnboarding,
} from '../../../onboarding/hooks/useOnboarding';
import type { LinkingMethod } from '../../useLinkingWorkflow';
import { readFailedAttempts, SUPPORT_RESCUE_AFTER_ATTEMPTS } from '../failedAttempts';
import { resolveDirectMethod } from './resolveLinkingEntry';
import {
	LINKING_ENTRY_FLAG,
	LINKING_RESCUE_FLAG,
	linkingEntryStampAnswer,
	linkingRescueStampAnswer,
	readLinkingEntryArm,
	readLinkingRescueArm,
	type ExperimentArm,
} from './linkingExperiments';

/**
 * Suelta el flujo como control si nada resuelve. Sin esta válvula, un CDN
 * bloqueado dejaría la pantalla de vinculación en blanco, que es la peor
 * superficie del producto para quedarse esperando.
 */
const RESOLVE_TIMEOUT_MS = 1000;

type Assignment = { arm: ExperimentArm; value: unknown } | null;

/**
 * Evalúa una feature de experimento UNA vez y devuelve el brazo sólo si la
 * REGLA de experimento corrió de verdad. Con la feature apagada, o con el
 * usuario forzado por una regla de override, `source` no es 'experiment' y
 * nadie entra a la cohorte.
 *
 * El `updateAttributes` sincrónico antes de evaluar es lo que quita la carrera
 * con `Protected.routes`: su efecto padre corre DESPUÉS de este en el primer
 * mount, así que sin esto el hash saldría sin `user_id` y el usuario se caería
 * del experimento sin que se viera como error.
 */
const assign = (flag: string, userId: number | undefined): Assignment => {
	if (userId) growthbookFile.updateAttributes({ user_id: userId });
	const result = growthbookFile.evalFeature(flag);
	if (result?.source !== 'experiment') return null;
	const variationId = result.experimentResult?.variationId;
	if (typeof variationId !== 'number') return null;
	return { arm: variationId === 0 ? 'control' : 'treatment', value: result.value };
};

export type LinkingEntry = {
	/** Hasta que sea `true` el flujo NO debe montar sus pasos: el paso inicial
	 * se decide una sola vez, en el `useState` del orquestador. */
	resolved: boolean;
	/** Método con el que abrir directo el paso 2, o `null` para el control. */
	directMethod: LinkingMethod | null;
};

/**
 * Asignación de los DOS experimentos del flujo, en un solo lugar, igual que el
 * host de onboarding v3 hace con phone-verification-optional. Todo lo demás
 * (incluido el paso 2) lee el record, no GrowthBook.
 *
 * El de rescate se asigna sólo con al menos un fallo encima: evaluar al entrar
 * metería a la cohorte a quien vincula al primer intento y nunca podría haber
 * visto el botón en ningún brazo, diluyendo el efecto hasta volverlo
 * indetectable.
 */
export const useLinkingExperiments = (enabled: boolean): LinkingEntry => {
	const featuresReady = useFeaturesReady();
	const { isMobile } = usePlatform();
	const userId = useSelector((state: RootState) => state.profile.user?.id);
	const { data: record, isPending: recordPending } = useOnboarding();
	const { mutateAsync: updateOnboarding } = useUpdateOnboarding();

	const [arm, setArm] = useState<ExperimentArm | null>(null);
	const [resolved, setResolved] = useState(false);
	const settled = useRef(false);
	const rescueAssigned = useRef(false);

	useEffect(() => {
		if (settled.current) return;

		if (!enabled) {
			settled.current = true;
			setResolved(true);
			return;
		}

		// El stamp manda. Sólo se consulta GrowthBook cuando no hay ninguno.
		const stamped = readLinkingEntryArm(record);
		if (stamped) {
			settled.current = true;
			setArm(stamped);
			setResolved(true);
			return;
		}

		if (featuresReady && !recordPending) {
			settled.current = true;
			const assignment = assign(LINKING_ENTRY_FLAG, userId);
			setArm(assignment?.arm ?? null);
			setResolved(true);
			if (assignment && record) {
				updateOnboarding({
					answers: linkingEntryStampAnswer(
						assignment.arm,
						isMobile ? 'mobile' : 'desktop',
					),
				}).catch(() => {});
			}
			return;
		}

		const timeoutId = window.setTimeout(() => {
			if (settled.current) return;
			settled.current = true;
			setResolved(true);
		}, RESOLVE_TIMEOUT_MS);

		return () => window.clearTimeout(timeoutId);
	}, [
		enabled,
		featuresReady,
		record,
		recordPending,
		userId,
		isMobile,
		updateOnboarding,
	]);

	// Rescate a soporte: misma mecánica, otra puerta de elegibilidad.
	useEffect(() => {
		if (rescueAssigned.current || !featuresReady || recordPending) return;
		if (readFailedAttempts() < 1) return;
		if (readLinkingRescueArm(record)) {
			rescueAssigned.current = true;
			return;
		}
		rescueAssigned.current = true;
		const assignment = assign(LINKING_RESCUE_FLAG, userId);
		if (!assignment || !record) return;
		const threshold =
			typeof assignment.value === 'number' && assignment.value > 0
				? assignment.value
				: SUPPORT_RESCUE_AFTER_ATTEMPTS;
		updateOnboarding({
			answers: linkingRescueStampAnswer(assignment.arm, threshold),
		}).catch(() => {});
	}, [featuresReady, record, recordPending, userId, updateOnboarding]);

	return {
		resolved,
		directMethod: arm === 'treatment' ? resolveDirectMethod(isMobile) : null,
	};
};

export default useLinkingExperiments;
